// 縁が降りた日の一枚（Phase B）。/top の一行の下に置く。
//   「〇〇神社の御祭神が、ひとつの縁を引き合わせました。」の下に、重なった社と二つの動作。
//   相手の名前・顔・日付は出さない（drop には otherUid と神社 ID しか無く、ここでは otherUid も使わない）。
//   言葉: 「縁を結びに、袖を振る」ではなく「縁を結ぶ」「見送る」。出会い・マッチングは出さない。
import { useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import useShrineNames from "./useShrineNames";

const SERIF = '"Hiragino Mincho ProN", "Yu Mincho", serif';
const WASHI = "#ece5d4";
const WASHI_DIM = "rgba(222,215,198,0.55)";
const GOLD_TEXT = "#e6d3a3";

const DOOR_SX = {
  fontFamily: SERIF,
  fontWeight: 600,
  px: 5,
  py: 1.3,
  fontSize: "0.9rem",
  letterSpacing: 4,
  textIndent: 4,
  color: GOLD_TEXT,
  bgcolor: "rgba(212,180,110,0.05)",
  border: "1px solid rgba(212,180,110,0.5)",
  borderRadius: 0,
  boxShadow: "0 0 24px rgba(212,180,110,0.1)",
  "&:hover": { bgcolor: "rgba(212,180,110,0.12)", borderColor: "rgba(224,201,143,0.9)", color: "#f2e5c0" },
};

/**
 * @param {object} props
 * @param {object} props.drop useDrops().today（state=new）
 * @param {(id:string)=>Promise<void>} props.onWave 縁を結ぶ
 * @param {(id:string)=>Promise<void>} props.onPass 見送る
 */
export default function TodayDrop({ drop, onWave, onPass }) {
  const [busy, setBusy] = useState(false);
  const names = useShrineNames(drop?.shrineIds || [], {});
  if (!drop) return null;
  const shrines = (drop.shrineIds || []).map((id) => names[id]?.name).filter(Boolean);

  const act = async (fn) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn(drop.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ textAlign: "center", mb: 5 }} data-testid="today-drop">
      <Typography sx={{ fontFamily: SERIF, fontSize: "0.8rem", letterSpacing: 1.5, lineHeight: 2.2, color: WASHI_DIM }}>
        {shrines.length > 0 ? (
          <>
            あなたと同じ、
            {shrines.map((n, i) => (
              <Box component="span" key={n} sx={{ color: WASHI }}>
                {i > 0 ? "、" : ""}
                {n}
              </Box>
            ))}
            に参った人です。
          </>
        ) : (
          "あなたと同じ社に参った人です。"
        )}
        {drop.timeBond && (
          <>
            <br />
            同じ日に、同じ社にいました。
          </>
        )}
        <br />
        顔も名前も、まだ分かりません。
      </Typography>

      <Stack spacing={1.5} alignItems="center" sx={{ mt: 3 }}>
        <Button size="large" sx={DOOR_SX} disabled={busy} onClick={() => act(onWave)}>
          縁を結ぶ
        </Button>
        <Button
          variant="text"
          size="small"
          disabled={busy}
          onClick={() => act(onPass)}
          sx={{ color: WASHI_DIM, fontSize: "0.78rem", fontFamily: SERIF, letterSpacing: 2, "&:hover": { color: WASHI } }}
        >
          見送る
        </Button>
      </Stack>
      <Typography sx={{ fontFamily: SERIF, fontSize: "0.7rem", letterSpacing: 1.5, color: "rgba(222,215,198,0.42)", mt: 2, lineHeight: 2 }}>
        相手も結べば、縁がつながります。
        <br />
        どちらを選んだかは、相手に伝わりません。
      </Typography>
    </Box>
  );
}
